import { i18n } from "../../translate/i18n";

import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Button, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { IconButton, InputAdornment, Switch } from "@mui/material";
import {
  Eye as Visibility,
  EyeOff as VisibilityOff,
  Lock as LockIcon,
  Mail as EmailIcon,
  MessageCircle as WhatsAppIcon
} from "lucide-react";
import { Helmet } from "react-helmet";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100%",
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      height: "auto",
      minHeight: "100dvh",
    },
  },
  brandingSide: {
    flex: 1,
    backgroundColor: "#88d03e",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "clamp(20px, 4vw, 56px)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "42vh",
    },
  },
  brandingContent: {
    width: "100%",
    maxWidth: "520px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    animationName: "$fadeIn",
    animationDuration: "0.7s",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
  },
  brandingLogo: {
    width: "clamp(140px, 22vw, 240px)",
    height: "auto",
    marginBottom: "clamp(18px, 2.8vh, 28px)",
    animation: "$floatLogo 4.2s ease-in-out infinite",
  },
  slogan: {
    fontSize: "clamp(30px, 3.4vw, 50px)",
    fontWeight: 700,
    color: "#1b1f22",
    fontFamily: "'EncampTest', 'Inter', sans-serif",
    lineHeight: 1.14,
    letterSpacing: "0.01em",
    maxWidth: "26ch",
    margin: 0,
  },
  sloganHighlight: {
    color: "#ffffff",
    fontWeight: 800,
  },
  formSide: {
    flex: 1,
    backgroundColor: "#ffffff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "clamp(24px, 4vw, 56px)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "58vh",
    },
  },
  formContainer: {
    width: "100%",
    maxWidth: "390px",
    display: "flex",
    flexDirection: "column",
    animationName: "$fadeIn",
    animationDuration: "0.7s",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      padding: 0,
    },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(20px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes floatLogo": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-6px)" },
    "100%": { transform: "translateY(0px)" },
  },
  formHeader: {
    marginBottom: "20px",
  },
  welcomeTitle: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#1b1f22",
    marginBottom: "4px",
    fontFamily: "'Poppins', 'Inter', sans-serif",
  },
  welcomeSubtitle: {
    fontSize: "14px",
    color: "rgba(27, 31, 34, 0.62)",
    fontFamily: "'Poppins', 'Inter', sans-serif",
  },
  inputField: {
    marginBottom: "14px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#ffffff",
      transition: "all 0.25s ease",
      "& fieldset": {
        borderColor: "rgba(27, 31, 34, 0.15)",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "rgba(27, 31, 34, 0.35)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#88d03e",
        borderWidth: "2px",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: "0 0 0 4px rgba(136, 208, 62, 0.14)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(27, 31, 34, 0.6)",
      "&.Mui-focused": {
        color: "#88d03e",
      },
    },
    "& .MuiInputBase-input": {
      padding: "15px 14px",
      fontSize: "15px",
      color: "#1b1f22",
    },
  },
  submitBtn: {
    marginTop: "14px",
    background: "#63ae18",
    color: "#1b1f22",
    borderRadius: "12px",
    padding: "12px",
    fontWeight: 600,
    fontSize: "15px",
    width: "100%",
    textTransform: "none",
    boxShadow: "0 8px 18px rgba(99, 174, 24, 0.28)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#88d03e",
      boxShadow: "0 10px 24px rgba(136, 208, 62, 0.35)",
      transform: "translateY(-2px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },
  forgotPassword: {
    marginTop: "14px",
    textAlign: "center",
  },
  forgotPasswordLink: {
    color: "#63ae18",
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "14px",
    transition: "color 0.2s ease",
    "&:hover": { 
      color: "#88d03e",
    },
  },
  rememberMeContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: "8px",
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#88d03e",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#88d03e",
    },
  },
  rememberText: {
    fontSize: "14px",
    color: "rgba(27, 31, 34, 0.65)",
    marginLeft: "4px",
  },
  whatsappButton: {
    position: "fixed",
    bottom: "clamp(12px, 2vh, 24px)",
    right: "clamp(12px, 2vw, 24px)",
    background: "linear-gradient(135deg, #88d03e 0%, #63ae18 100%)",
    borderRadius: "50%",
    width: "56px",
    height: "56px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 20px rgba(136, 208, 62, 0.45)",
    cursor: "pointer",
    zIndex: 999,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.1) translateY(-2px)",
      boxShadow: "0 8px 30px rgba(136, 208, 62, 0.6)",
    },
    "&:active": {
      transform: "scale(1.05)",
    },
  },
  whatsappIcon: {
    fontSize: 28,
    color: "#1b1f22",
  },
  iconAdornment: {
    color: "rgba(27, 31, 34, 0.55)",
  },
}));

const Login = () => {
  const classes = useStyles();
  const { handleLogin } = useContext(AuthContext);

  const [user, setUser] = useState({
    email: "",
    password: "",
    remember: false,
  });

  const [branding, setBranding] = useState({
    loginWhatsapp: "https://wa.me/5500000000000",
  });

  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [playIntroAnimation] = useState(() => {
    try {
      return sessionStorage.getItem("loginIntroAnimationPlayed") !== "1";
    } catch (e) {
      return true;
    }
  });

  // ========= Tema e aparência da página =============
  useEffect(() => {
    try {
      localStorage.setItem("theme", "light");
    } catch (e) {}
    document.documentElement.classList.remove("dark");
    document.documentElement.setAttribute("data-theme", "light");
    document.body.classList.add("login-page");
    return () => document.body.classList.remove("login-page");
  }, []);

  useEffect(() => {
    if (!playIntroAnimation) return;
    try {
      sessionStorage.setItem("loginIntroAnimationPlayed", "1");
    } catch (e) {}
  }, [playIntroAnimation]);

  // ========= Buscar settings globais ============
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");

        setBranding({
          loginWhatsapp: data.loginWhatsapp || "https://wa.me/5500000000000",
        });
      } catch (err) {
        console.error("Erro ao carregar branding:", err);
      }
    };

    fetchBranding();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const lang = localStorage.getItem("i18nextLng") || "pt";
    i18n.changeLanguage(lang);
    handleLogin(user);
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>

      <div className={classes.root}>
        <div className={classes.brandingSide}>
          <div className={classes.brandingContent}>
            <img
              src="/login_logo.png"
              alt="Logo"
              className={classes.brandingLogo}
            />
            <Typography className={classes.slogan}>
              Transformando <span className={classes.sloganHighlight}>conversas</span> em <span className={classes.sloganHighlight}>negócios</span>.
            </Typography>
          </div>
        </div>

        <div className={classes.formSide}>
          <form className={classes.formContainer} onSubmit={handleSubmit}>
            <div className={classes.formHeader}>
              <Typography className={classes.welcomeTitle}>
                Bem-vindo de volta
              </Typography>
              <Typography className={classes.welcomeSubtitle}>
                Faça login para continuar
              </Typography>
            </div>

            {error && (
              <Typography 
                color="error" 
                style={{ 
                  marginBottom: 16, 
                  textAlign: "center",
                  padding: "12px",
                  background: "rgba(99, 174, 24, 0.08)",
                  border: "1px solid rgba(99, 174, 24, 0.3)",
                  borderRadius: "10px",
                  fontSize: "14px",
                }}
              >
                {error}
              </Typography>
            )}

            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              className={classes.inputField}
              type="email"
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon className={classes.iconAdornment} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Senha"
              variant="outlined"
              fullWidth
              className={classes.inputField}
              type={showPassword ? "text" : "password"}
              value={user.password}
              onChange={(e) => setUser({ ...user, password: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon className={classes.iconAdornment} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      style={{ color: "#63ae18" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.rememberMeContainer}>
              <Switch
                checked={user.remember}
                onChange={(e) =>
                  setUser({ ...user, remember: e.target.checked })
                }
                size="small"
              />
              <Typography className={classes.rememberText}>
                Lembrar de mim
              </Typography>
            </div>

            <Button
              type="submit"
              variant="contained"
              className={classes.submitBtn}
              disableElevation
            >
              Entrar
            </Button>

            <div className={classes.forgotPassword}>
              <RouterLink
                to="/forgot-password"
                className={classes.forgotPasswordLink}
              >
                Esqueceu sua senha?
              </RouterLink>
            </div>
          </form>
        </div>

        {/* ===== BOTÃO WHATSAPP DINÂMICO ===== */}
        <div
          className={classes.whatsappButton}
          onClick={() => window.open(branding.loginWhatsapp)}
        >
          <WhatsAppIcon className={classes.whatsappIcon} />
        </div>
      </div>
    </>
  );
};

export default Login;
